import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const SignupMessage = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #323232;
  text-align: center;
  margin-top: 2rem;
`;

/**
 * Prominent lock UI shown when the AOTD claim is blocked by another
 * player's active card effect. Live countdown when `unlockAt` is known.
 * Extracts the inline `ClaimBlockedLockMessage` from the monolith
 * (lines 1752–1850) verbatim.
 */
export interface ClaimBlockedStateProps {
  ownerUsername?: string;
  cardSlug?: string;
  cardName?: string;
  unlockAt?: string;
  message?: string;
  onRetry: () => void;
}

export const ClaimBlockedState: React.FC<ClaimBlockedStateProps> = ({
  ownerUsername,
  cardSlug,
  cardName,
  unlockAt,
  onRetry,
}) => {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!unlockAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [unlockAt]);

  const unlockMs = unlockAt ? new Date(unlockAt).getTime() : null;
  const remainingMs = unlockMs ? unlockMs - now : null;

  let countdown: string | null = null;
  if (remainingMs !== null && remainingMs > 0) {
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    countdown = [hours, minutes, seconds].map((v) => v.toString().padStart(2, "0")).join(":");
  }

  const cardLabel = cardName || cardSlug || "another card";
  const ownerLabel = ownerUsername ? `@${ownerUsername}` : "another player";
  const unlockTime = unlockMs
    ? new Date(unlockMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <SignupMessage style={{ display: "flex", justifyContent: "center" }}>
      <div className="respect-aotd-lock" role="alert">
        <p className="respect-aotd-lock__title">
          🔒 The AOTD is locked by {cardLabel}{" "}
          {ownerUsername ? <>(played by {ownerLabel})</> : null}.
        </p>
        <p className="respect-aotd-lock__sub">
          Another player activated a card that controls who can claim today&apos;s AOTD. This is intentional —
          try again when the lock lifts.
        </p>
        {countdown ? (
          <p className="respect-aotd-lock__sub">
            Try again in <span className="respect-aotd-lock__countdown">{countdown}</span>
            {unlockTime ? <> (at {unlockTime})</> : null}.
          </p>
        ) : unlockTime ? (
          <p className="respect-aotd-lock__sub">Try again at {unlockTime}.</p>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            flexWrap: "wrap",
            marginTop: "8px",
          }}
        >
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: "10px 20px",
              background: "#444",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontWeight: "bold",
            }}
          >
            Check again
          </button>
          {cardSlug ? (
            <Link
              to="/cards"
              style={{
                padding: "10px 16px",
                background: "transparent",
                color: "#4f3e76",
                border: "1px solid #4f3e76",
                borderRadius: "4px",
                fontWeight: 600,
                textDecoration: "none",
                fontSize: "0.9rem",
              }}
            >
              → Browse cards
            </Link>
          ) : null}
        </div>
      </div>
    </SignupMessage>
  );
};

export default ClaimBlockedState;
